/**
 * Product Hotspot Features component
 * Handles tab switching, hotspot clicks, and carousel navigation
 */
class ProductHotspotFeatures extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    // Initialize tabs
    const tabs = this.querySelectorAll('.product-hotspot-features__tab');
    for (const tab of tabs) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e), { signal });
    }

    // Initialize hotspots
    const hotspots = this.querySelectorAll('.product-hotspot-features__hotspot');
    for (const hotspot of hotspots) {
      hotspot.addEventListener('click', (e) => this.#handleHotspotClick(e), { signal });
    }

    // Initialize carousel navigation
    const prevButtons = this.querySelectorAll('.product-hotspot-features__nav-btn--prev');
    const nextButtons = this.querySelectorAll('.product-hotspot-features__nav-btn--next');

    for (const btn of prevButtons) {
      btn.addEventListener('click', (e) => this.#handleCarouselNav(e, -1), { signal });
    }

    for (const btn of nextButtons) {
      btn.addEventListener('click', (e) => this.#handleCarouselNav(e, 1), { signal });
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleTabClick(event) {
    const tab = event.currentTarget;
    const targetPanelId = tab.dataset.panelTarget;

    // Update tab states
    const allTabs = this.querySelectorAll('.product-hotspot-features__tab');
    for (const t of allTabs) {
      t.classList.remove('product-hotspot-features__tab--active');
      t.setAttribute('aria-selected', 'false');
    }
    tab.classList.add('product-hotspot-features__tab--active');
    tab.setAttribute('aria-selected', 'true');

    // Update image panels
    const allImagePanels = this.querySelectorAll('.product-hotspot-features__image-panel');
    for (const panel of allImagePanels) {
      panel.classList.remove('product-hotspot-features__image-panel--active');
    }
    const targetImagePanel = this.querySelector(
      `.product-hotspot-features__image-panel[data-panel-id="${targetPanelId}"]`
    );
    if (targetImagePanel) {
      targetImagePanel.classList.add('product-hotspot-features__image-panel--active');
    }

    // Update content panels
    const allContentPanels = this.querySelectorAll('.product-hotspot-features__panel');
    for (const panel of allContentPanels) {
      panel.classList.remove('product-hotspot-features__panel--active');
      panel.hidden = true;
    }
    const targetContentPanel = this.querySelector(
      `.product-hotspot-features__panel[data-panel-id="${targetPanelId}"]`
    );
    if (targetContentPanel) {
      targetContentPanel.classList.add('product-hotspot-features__panel--active');
      targetContentPanel.hidden = false;
      this.#resetCarousel(targetContentPanel);
    }
  }

  #handleHotspotClick(event) {
    const hotspot = event.currentTarget;
    const imagePanel = hotspot.closest('.product-hotspot-features__image-panel');
    if (!imagePanel) return;

    const hotspotIndex = parseInt(hotspot.dataset.hotspotIndex || '0', 10);

    // Update hotspot states within this panel
    const allHotspots = imagePanel.querySelectorAll('.product-hotspot-features__hotspot');
    for (const h of allHotspots) {
      h.classList.remove('product-hotspot-features__hotspot--active');
    }
    hotspot.classList.add('product-hotspot-features__hotspot--active');

    // Navigate carousel to corresponding slide
    const panelId = imagePanel.dataset.panelId;
    if (panelId) {
      const contentPanel = this.querySelector(`.product-hotspot-features__panel[data-panel-id="${panelId}"]`);
      if (contentPanel) {
        this.#goToSlide(contentPanel, hotspotIndex);
      }
    }
  }

  #handleCarouselNav(event, direction) {
    const target = event.currentTarget;
    const panel = target.closest('.product-hotspot-features__panel');
    if (!panel) return;

    const slides = Array.from(panel.querySelectorAll('.product-hotspot-features__slide'));
    const activeSlide = panel.querySelector('.product-hotspot-features__slide--active');

    if (!activeSlide || slides.length === 0) return;

    // Find current position in slides array
    const currentArrayIndex = slides.indexOf(activeSlide);
    let newArrayIndex = currentArrayIndex + direction;

    // Wrap around
    if (newArrayIndex < 0) newArrayIndex = slides.length - 1;
    if (newArrayIndex >= slides.length) newArrayIndex = 0;

    // Get the actual slide index from the target slide's data attribute
    const targetSlide = slides[newArrayIndex];
    const newSlideIndex = parseInt(targetSlide.dataset.slideIndex || '0', 10);

    this.#goToSlide(panel, newSlideIndex);
    this.#updateHotspotFromSlide(panel, newSlideIndex);
  }

  #goToSlide(panel, index) {
    const slides = panel.querySelectorAll('.product-hotspot-features__slide');

    for (const slide of slides) {
      slide.classList.remove('product-hotspot-features__slide--active');
    }

    const targetSlide = panel.querySelector(`[data-slide-index="${index}"]`);
    if (targetSlide) {
      targetSlide.classList.add('product-hotspot-features__slide--active');
    }
  }

  #updateHotspotFromSlide(contentPanel, slideIndex) {
    const panelId = contentPanel.dataset.panelId;
    const imagePanel = this.querySelector(`.product-hotspot-features__image-panel[data-panel-id="${panelId}"]`);

    if (!imagePanel) return;

    const allHotspots = imagePanel.querySelectorAll('.product-hotspot-features__hotspot');
    for (const h of allHotspots) {
      h.classList.remove('product-hotspot-features__hotspot--active');
    }

    const targetHotspot = imagePanel.querySelector(`[data-hotspot-index="${slideIndex}"]`);
    if (targetHotspot) {
      targetHotspot.classList.add('product-hotspot-features__hotspot--active');
    }
  }

  #resetCarousel(panel) {
    if (!panel) return;

    const firstSlide = panel.querySelector('.product-hotspot-features__slide');
    if (firstSlide) {
      const firstSlideIndex = parseInt(firstSlide.dataset.slideIndex || '0', 10);
      this.#goToSlide(panel, firstSlideIndex);
      this.#updateHotspotFromSlide(panel, firstSlideIndex);
    }
  }
}

if (!customElements.get('product-hotspot-features')) {
  customElements.define('product-hotspot-features', ProductHotspotFeatures);
}
